import {
  Box,
  SimpleGrid,
  Center,
  Divider,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { Formik, FormikProps, Form } from 'formik';
import lodash from 'lodash';
import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import { SettingsUserFormValues } from 'types/dashboard';
import { Maybe } from 'types/dashboard-core-gqlTypes.generated';

import {
  SettingsFormField,
  WhiteSubmitButton,
} from 'packages/common-frontend/components/FormField';
import { LabeledDetail } from 'packages/common-frontend/components/LabeledDetail';
import {
  toastId,
  successToastConfig,
  errorToastId,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { useUpdateUserSettingsMutation } from 'packages/user/requests/user.gql.generated';
import { SettingsEditButton } from 'packages/user/ui/SettingsEditButton';
import { FiSave, FiXSquare } from 'react-icons/fi';

const { capitalize } = lodash;

export const SettingsEditForm = ({
  user,
  setEditMode,
}: {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    roleType: string;
    organization: {
      name: string;
      industry?: Maybe<string>;
    };
  };
  setEditMode: Function;
}) => {
  const [userInfo, setUserInfo] = useState({
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
  };
  const formValidation = Yup.object().shape({
    email: Yup.string()
      .email('Email address must be in a valid format')
      .required('Required'),
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
  });

  const toast = useToast();
  const history = useHistory();

  const [mutate] = useUpdateUserSettingsMutation();

  const handleChange = (e: React.ChangeEvent<any>) => {
    setUserInfo({ ...userInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      const { data } = await mutate({
        variables: {
          email: userInfo.email,
          id: userInfo.id,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
        },
      });

      if (data && data.updateUserSettings && !toast.isActive(toastId)) {
        const response = data.updateUserSettings.response;
        successToastConfig.title = response;
        // don't toast for now, since this history.go will refresh
        // the settings page
        // toast(successToastConfig);
        setIsSubmitting(false);
        history.go(0);
      }
    } catch (err: any) {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = 'Error updating user settings.';
        toast(errorToastConfig);
        setIsSubmitting(false);
      }
      throw err;
    }
  }, [mutate, userInfo, history, toast]);

  const handleClose = () => {
    setEditMode(false);
  };

  return (
    <Box>
      <SimpleGrid columns={{ sm: 1, md: 2 }} spacingX={10} spacingY="20px">
        <LabeledDetail label="Organization">
          {user.organization.name}
        </LabeledDetail>
        <LabeledDetail label="Industry">
          {user.organization.industry}
        </LabeledDetail>
      </SimpleGrid>

      <Center height="50px">
        <Divider />
      </Center>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={formValidation}
      >
        {(formProps: FormikProps<SettingsUserFormValues>) => {
          return (
            <Form data-testid="settings-user-edit-form">
              <SimpleGrid
                columns={{ sm: 1, md: 2 }}
                spacingX={10}
                spacingY="20px"
              >
                <SettingsFormField
                  isRequired
                  handleChange={handleChange}
                  propName="firstName"
                  label="First Name"
                  placeholder="John"
                  inputWidth="80%"
                  formProps={formProps}
                />
                <SettingsFormField
                  isRequired
                  handleChange={handleChange}
                  propName="lastName"
                  label="Last Name"
                  inputWidth="80%"
                  placeholder="Doe"
                  formProps={formProps}
                />
                <SettingsFormField
                  isRequired
                  handleChange={handleChange}
                  propName="email"
                  label="Email"
                  type="email"
                  placeholder="john@email.com"
                  inputWidth="80%"
                  formProps={formProps}
                />
                <LabeledDetail label="Role">
                  {capitalize(user.roleType)}
                </LabeledDetail>
              </SimpleGrid>

              <Box height={50} />
              <HStack spacing={5}>
                <WhiteSubmitButton
                  isLoading={isSubmitting}
                  label="Save"
                  rightIcon={<FiSave />}
                />

                <SettingsEditButton
                  label="Close"
                  icon={<FiXSquare />}
                  onClickHandler={handleClose}
                />
              </HStack>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};
